// Slug-naming-convention input (item 62 / delivery #71).
//
// Drop-in replacement for the plain <Input> in project + repository
// create forms. Live-validates against the canonical regex on each
// keystroke, surfaces an inline error + best-effort suggestion that
// the operator can apply with one click, and exposes an `error` flag
// so the parent form can keep the submit button disabled until the
// slug is compliant.
//
// Why on the form rather than only on the wire:
//   - the API returns the same structured error for a bad slug, but
//     making the user wait for a 400 round-trip after typing the full
//     name + description fields is a poor experience;
//   - by mirroring the validator here the UX sees the failure at the
//     keystroke that introduced it.

import { useMemo } from 'react';
import { Input } from './ui/Input';
import {
  SLUG_RULE_HINT,
  suggestSlug,
  validateSlug,
} from '../lib/slug';

interface SlugInputProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  // Non-empty value is rendered as-is. Empty value passes through to
  // the inner <Input> placeholder so the form skeleton stays uniform.
  readonly className?: string;
}

export function SlugInput({
  value,
  onChange,
  placeholder = 'slug (e.g. acme.platform)',
  required,
  disabled,
  className,
}: SlugInputProps) {
  const error = useMemo(() => (value === '' ? null : validateSlug(value)), [value]);
  const suggestion = useMemo(
    () => (error ? suggestSlug(value) : null),
    [error, value],
  );
  return (
    <div className={className}>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={error ? 'true' : undefined}
        className={error ? 'border-danger focus:border-danger' : ''}
      />
      {error ? (
        <p className="text-xs text-danger mt-1">
          {error}
          {suggestion && (
            <>
              {' '}
              Did you mean{' '}
              <button
                type="button"
                onClick={() => onChange(suggestion)}
                className="font-mono underline hover:text-danger/80"
              >
                {suggestion}
              </button>
              {'?'}
            </>
          )}
        </p>
      ) : (
        <p className="text-xs text-fg-subtle mt-1">{SLUG_RULE_HINT}</p>
      )}
    </div>
  );
}

// slugIsValid is a thin re-export so callers (e.g. the form's submit
// gate) can keep the import surface to a single module.
export function slugIsValid(slug: string): boolean {
  return validateSlug(slug) === null;
}
